import { Form, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { DateRangePicker } from 'src/components/DateRangePicker/DateRangePicker';
import { SearchPanel } from 'src/components/SearchPanel/SearchPanel';
import {
    SearchPanelFilterForm,
    SearchPanelFilterFormProps,
} from 'src/components/SearchPanel/SearchPanelFilterForm';
import { getCollectionStatuses } from 'src/pages/CollectionsPage/CollectionsStatusInfoMap';
import {
    CollectionsFilter,
    CollectionsStore,
} from 'src/stores/CollectionsStore/CollectionsStore';
import { DateHelper } from 'src/utils/DateHelper';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

export const CollectionsHeaderPanel = observer(() => {
    const { t } = useTranslation();

    return (
        <StyledHeaderWrapper>
            <SearchPanel
                inputSearchFormName={formNames.getPath('filterSearch')}
                store={store}
                renderPanel={(props) => <FilterPanel {...props} />}
                inputSearchPlaceholder={t(
                    'Page.Collections.AsideHeader.Search.Placeholder',
                )}
                data-testid="collections-search-panel"
            />
        </StyledHeaderWrapper>
    );
});

const StyledHeaderWrapper = styled.header`
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;
`;

export const FilterPanel = observer((props: SearchPanelFilterFormProps) => {
    const statuses = getCollectionStatuses();
    const { t } = useTranslation();

    return (
        <SearchPanelFilterForm
            {...props}
            onFinish={(values: FilterWithDateRange) => {
                store.pager.reset();
                store.filterCriteria?.applyFilter(
                    convertFormValuesToFilterCriteria(values),
                );
                store.loadList();
            }}
        >
            <StyledSelect
                label={t('Page.Collections.Filter.Status')}
                name={formNames.getPath('filterStatuses')}
            >
                <Select mode="multiple" allowClear data-testid="filterStatuses">
                    {statuses.map((item) => (
                        <Select.Option
                            value={item.id}
                            key={item.id}
                            data-testid={`${item.id}`}
                        >
                            {t(`data:status.collection.${item.id}`)}
                        </Select.Option>
                    ))}
                </Select>
            </StyledSelect>
            <Form.Item
                label={t('Page.Collections.Filter.IssuedAt')}
                name={formNames.getPath(dateRangePickerName)}
            >
                <DateRangePicker data-testid={dateRangePickerName} />
            </Form.Item>
        </SearchPanelFilterForm>
    );
});

const store = CollectionsStore;
const formNames = FormHelper.createNameProvider<FilterWithDateRange>();
const dateRangePickerName = 'filterStartedAt';

type FilterWithDateRange = Omit<
    CollectionsFilter,
    'filterStartedAtFrom' | 'filterStartedAtTo'
> & {
    [dateRangePickerName]: [moment.Moment, moment.Moment];
};

const convertFormValuesToFilterCriteria = (
    values: FilterWithDateRange,
): CollectionsFilter => {
    const { filterStartedAt, ...otherValues } = values;
    const [filterStartedAtFrom, filterStartedAtTo] = filterStartedAt || [
        undefined,
        undefined,
    ];

    return {
        ...otherValues,
        filterStartedAtFrom: filterStartedAtFrom
            ? DateHelper.formatToISO(filterStartedAtFrom.startOf('day'))
            : undefined,
        filterStartedAtTo: filterStartedAtTo
            ? DateHelper.formatToISO(filterStartedAtTo.endOf('day'))
            : undefined,
    };
};

const StyledSelect = styled(Form.Item)`
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
        display: flex;
        align-items: center;
        min-height: 2.7rem;
        border-radius: 0.5rem;
    }
`;
